<template>
  <div class="sidebar">
    <transition name="slide">
      <div v-if="isSidebarOpen" class="sidebar-mobile">
        <div class="sidebar-mobile__content">
          <SelectUser variant="mobile" @change-user="changeUser" @close-sidebar="close" />
          <div v-if="arePersonalDataVisible" class="sidebar-mobile__links">
            <div class="sidebar-mobile__group">
              <span>{{ $t("dashboard.group") }}</span>
              <div v-if="hasGroup && student.group.parentFullName" class="font-bold">
                {{ student.group.parentFullName }}
              </div>
            </div>
            <div class="sidebar-desktop__level">
              <span>{{ $t("dashboard.level") }}</span>
              <div v-if="hasGroup && student.group.realizationLanguageLevel" class="font-bold">
                {{ student.group.realizationLanguageLevel }}
              </div>
            </div>
            <div class="sidebar-mobile__lector">
              <router-link class="sidebar-mobile__contact-link contact-page-link" :to="{ name: 'Auth.Contact' }">
                <span class="mr-2">{{ $t("dashboard.lector") }}</span>
                <div class="font-bold" v-if="hasGroup">
                  <span v-if="student.group.lector?.firstName">{{
                    student.group.lector.firstName
                  }}</span>
                  {{ " " }}
                  <span v-if="student.group.lector?.lastName">{{
                    student.group.lector.lastName
                  }}</span>
                </div>
                <span class="sidebar-desktop__contact-label">{{ $t("dashboard.contact") }}</span>
              </router-link>
            </div>
          </div>
          <div :class="!arePersonalDataVisible && 'mt-12'">
            <AccountNavigation :routeName="currentRouteName" />
          </div>
          <div class="sidebar-mobile__policy">
            <!-- <a :href="paymentsHelpCenterLink" class="regulations-sidebar" target="_blank">{{
              $t("dashboard.payments_help_center")
            }}</a> -->
            <a
              href="/Polityka_Prywatności_ES_Online.pdf"
              target="_blank"
              class="forgotPassword__policy--item"
              >{{ $t("dashboard.privacy_and_cookies") }}</a
            >
            <a :href="REGULATIONS_PATH" target="_blank" class="forgotPassword__policy--item">{{
              $t("dashboard.regulations")
            }}</a>
          </div>
        </div>
      </div>
    </transition>

    <div class="sidebar-desktop">
      <div class="sidebar-desktop__content">
        <SelectUser variant="desktop" @change-user="changeUser" @close-sidebar="close" />
        <div class="sidebar-desktop__menu">
          <template v-if="arePersonalDataVisible">
            <div class="sidebar-desktop__group">
              <span>{{ $t("dashboard.group") }}</span>
              <div v-if="hasGroup && student.group.parentFullName" class="font-bold">
                {{ student.group.parentFullName }}
              </div>
            </div>
            <div class="sidebar-desktop__level">
              <span>{{ $t("dashboard.level") }}</span>
              <div v-if="hasGroup && student.group.realizationLanguageLevel" class="font-bold">
                {{ student.group.realizationLanguageLevel }}
              </div>
            </div>
            <div class="sidebar-desktop__lector">
              <router-link class="sidebar-desktop__contact-link contact-page-link" :to="{ name: 'Auth.Contact' }">
                <span>{{ $t("dashboard.lector") }}</span>
                <div v-if="hasGroup" class="font-bold sidebar-desktop__lector-name">
                  <span v-if="student.group.lector?.firstName">{{
                    student.group.lector.firstName
                  }}</span>
                  {{ " " }}
                  <span v-if="student.group.lector?.lastName">{{
                    student.group.lector.lastName
                  }}</span>
                </div>
                <span class="sidebar-desktop__contact-label">{{ $t("dashboard.contact") }}</span>
              </router-link>
            </div>
          </template>
          <router-link v-if="isHomeworkCardVisible" :to="{ name: 'Main.Homeworks' }"
            active-class="active homeworks-active" class="homework-card">
            <img class="homework-card" src="@/assets/images/icons/homeworks.svg" />
            <span class="homework-card">{{ $t("dashboard.homeworks") }}</span>
          </router-link>
          <router-link v-if="isAttendancesCardVisible" class="attendances-card" :to="{ name: 'Main.Attendances' }"
            exact-active-class="active attendances-active">
            <img class="attendances-card" src="@/assets/images/icons/attendances.svg" />
            <span class="attendances-card">{{ $t("dashboard.attendances") }}</span>
          </router-link>
          <router-link v-if="isGradesCardVisible" :to="{ name: 'Main.Grades' }"
            exact-active-class="active grades-active" class="grades-card">
            <img class="grades-card" src="@/assets/images/icons/grades.svg" />
            <span class="grades-card">{{ $t("dashboard.grades") }}</span>
          </router-link>
          <router-link v-if="isMaterialsCardVisible" class="materials-card"
            :to="{ name: 'Main.OnlineMaterials' }"
            exact-active-class="active materials-active">
            <img class="materials-card" src="@/assets/images/icons/materials.svg" />
            <span class="materials-card">{{ $t("dashboard.maretials_online") }}</span>
          </router-link>
        </div>
        <AccountNavigation :routeName="currentRouteName" />
        <div class="sidebar-desktop__links">
          <!-- <a :href="paymentsHelpCenterLink" class="regulations-sidebar" target="_blank">{{
            $t("dashboard.payments_help_center")
          }}</a> -->
          <a
            href="/Polityka_Prywatności_ES_Online.pdf"
            class="privacy-policy-sidebar"
            target="_blank"
            >{{ $t("dashboard.privacy_and_cookies") }}</a
          >
          <a :href="REGULATIONS_PATH" class="regulations-sidebar" target="_blank">{{
            $t("dashboard.regulations")
          }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import * as Sentry from '@sentry/vue';

import SelectUser from '../SelectUser.vue';
import AccountNavigation from './AccountNavigation.vue';

class MissingLectorDataError extends Error {
  constructor(message = '') {
    super(message);
    this.name = 'Niekompletne dane lektora';
  }
}

export default {
  name: 'Sidebar',
  components: {
    SelectUser,
    AccountNavigation,
  },
  methods: {
    ...mapActions('lessons', ['clearLesson', 'clearLessons']),
    ...mapActions('homeworks', ['clearHomeworks']),
    ...mapActions('grades', ['clearGrades']),
    ...mapActions('students', ['clearStudent', 'clearStudents']),
    ...mapActions('attendances', ['clearAttendances']),
    ...mapActions('materials', ['clearAllMaterials', 'clearRealizations']),
    ...mapActions('auth', ['clearCurrentProfile']),
    ...mapMutations('layout', ['closeSidebar']),

    close() {
      this.closeSidebar();
    },

    clearStore() {
      this.clearLesson();
      this.clearLessons();
      this.clearHomeworks();
      this.clearGrades();
      this.clearAttendances();
      this.clearStudent();
      this.clearRealizations();
      this.clearCurrentProfile();
    },

    async changeUser() {
      this.clearStore();
      await this.routerPush({ name: 'Auth.Welcome' });
    },

    missingLectorDataException() {
      Sentry.withScope((scope) => {
        const userId = this.user?.id || 'undefined';
        scope.setTag('user ID', userId);

        const studentId = this.getStudent?.id || 'undefined';
        scope.setTag('student ID', studentId);

        const lectorData = this.student?.group?.lector;
        const { firstName, lastName, id } = lectorData || {};
        const lectorTag = lectorData
          ? `${firstName || ''} | ${lastName || ''} | ${id || ''}`
          : 'undefined';

        scope.setTag('lector DATA', lectorTag);

        scope.setLevel('info');
        Sentry.captureException(new MissingLectorDataError(`User ID: ${userId}`));
      });
    },
  },

  computed: {
    ...mapState('auth', ['user']),
    ...mapState('students', ['student']),
    ...mapState('layout', ['isSidebarOpen']),
    ...mapGetters('auth', [
      'isOfficeLogin',
      'isFullAccess',
      'isDemoAccess',
      'isEmptyAccess',
      'isAgentProfile',
      'isOnlyAgentRole',
      'isAdditionalAccessProfile',
      'getIsLogoutBlocked',
    ]),
    ...mapGetters('students', ['getStudent']),

    currentRouteName() {
      return this.$route.name;
    },

    hasGroup() {
      return !!this.student?.group;
    },

    isChangeUserVisible() {
      return !this.isOnlyAgentRole && !this.isAdditionalAccessProfile;
    },

    arePersonalDataVisible() {
      return !this.isAgentProfile && !this.isAdditionalAccessProfile && this.isFullAccess;
    },

    isHomeworkCardVisible() {
      return (
        // eslint-disable-next-line no-prototype-builtins
        (this.isEmptyAccess && !this.isAgentProfile && !this.isAdditionalAccessProfile && this.student?.group?.hasOwnProperty('hasHomeworks')
          && this.student.group.hasHomeworks)
        || (this.isFullAccess
          // eslint-disable-next-line no-prototype-builtins
          && this.student?.group?.hasOwnProperty('hasHomeworks')
          && this.student.group.hasHomeworks)
      );
    },

    isAttendancesCardVisible() {
      return !this.isDemoAccess && !this.isAgentProfile && !this.isAdditionalAccessProfile;
    },

    isGradesCardVisible() {
      return (
        // eslint-disable-next-line no-prototype-builtins
        (this.isEmptyAccess && !this.isAgentProfile && !this.isAdditionalAccessProfile & this.student?.group?.hasOwnProperty('hasGrades')
          && this.student.group.hasGrades)
        || (this.isFullAccess
          // eslint-disable-next-line no-prototype-builtins
          && this.student?.group?.hasOwnProperty('hasGrades')
          && this.student.group.hasGrades)
      );
    },

    isMaterialsCardVisible() {
      if (this.isAgentProfile || this.isAdditionalAccessProfile) return true;
      const exception = 'brak';
      const screenTitle = this.student?.group?.realizationEdTechLinks;
      if (screenTitle && screenTitle === exception) return false;
      return true;
    },

    paymentsHelpCenterLink() {
      const { paymentUrl } = this.student || '';

      if (!paymentUrl) {
        return 'https://test-platnosci.earlystage.pl/centrum-pomocy';
      }

      const url = new URL(paymentUrl);

      const baseUrl = url.origin + url.pathname;
      const queryParams = url.search;

      return `${baseUrl}centrum-pomocy/${queryParams}`;
    },

    selectUserLabel() {
      if (this.isAgentProfile || this.isAdditionalAccessProfile) {
        return this.$t('dashboard.choose_profile');
      }
      return this.$t('dashboard.choose_student');
    },
  },
  mounted() {
    const lectorData = this.student?.group?.lector;
    const { firstName, lastName } = lectorData || {};
    if (this.hasGroup && (!lectorData || !firstName || !lastName)) {
      this.missingLectorDataException();
    }
  },
  created() {
    this.close();
  },
};
</script>

<style lang="scss">
.inactive {
  @apply opacity-25 pointer-events-none;
}

.inactive-base {
  @apply pointer-events-none;
}

.sidebar-mobile {
  @apply fixed bg-white overflow-y-auto h-screen z-40 px-6 py-8;
  box-shadow: 0 5px 10px 0 hsla(0, 2%, 49%, 0.2);
  left: 0;
  top: 0;
  width: 300px;

  &__content {
    @apply flex flex-col;
  }

  &__info {
    @apply flex items-center mb-3;
  }

  &__avatar {
    @apply mx-0 p-0 bg-white border-solid border-grey overflow-hidden;
    cursor: pointer;
    width: 31px;
    height: 31px;
    border-width: 3px;
    border-radius: 2px;

    img {
      object-fit: contain;
      max-width: 25px;
    }
  }

  &__name {
    @apply pl-3 text-left font-bold;
    cursor: pointer;

    span {
      display: inline-block;
      width: 100%;
    }
  }

  &__select-student {
    @apply text-sm text-left text-grey-dark;
    cursor: pointer;
    display: block;
  }

  &__arrow {
    @apply absolute flex flex-1 justify-end items-center p-2;
    top: 8px;
    right: 8px;

    img {
      @apply cursor-pointer;
    }
  }

  &__group {
    @apply leading-normal;
    margin-bottom: 15px;
  }

  &__links {
    @apply flex flex-col pt-8 leading-loose;
    margin-bottom: 46px;

    a {
      @apply no-underline pb-1 flex items-center;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    img {
      @apply pr-5 h-5;
    }
  }

  &__lector {
    @apply leading-normal;

    a {
      align-items: flex-start;
    }
  }

  &__contact-link {
    @apply mb-4;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: 0;
  }

  &__policy {
    @apply flex flex-col leading-loose;

    a {
      @apply py-0 no-underline flex items-center text-grey-dark text-sm;
    }

    button {
      @apply text-black;
      text-align: left;
      font-size: 16px;
      display: block;
      width: 100%;
      margin-top: 20px;
    }
  }

  &__change-student {
    @apply mb-1 flex items-center leading-loose;

    img {
      @apply pr-5 h-5;
    }
  }

  &__logout {
    @apply mb-3 flex items-center leading-loose;

    img {
      @apply pr-6 h-5;
    }
  }
}

.sidebar-desktop {
  @apply hidden fixed bg-white overflow-y-auto h-screen z-40 px-6 pt-8;
  left: 0;
  top: 0;
  width: 312px;

  @media screen and (min-width: 992px) {
    display: block;
  }

  &__content {
    @apply flex flex-col h-full;
  }

  &__info {
    @apply flex;
  }

  &__avatar {
    @apply mx-0 p-0 bg-white border-solid border-grey overflow-hidden;
    width: 70px;
    cursor: pointer;
    height: 70px;
    border-width: 3px;
    border-radius: 20px;

    img {
      object-fit: cover;
      max-width: 64px;
    }
  }

  &__name {
    @apply flex flex-col justify-center items-start flex-1 font-bold pl-6;
    cursor: pointer;
  }

  &__arrow {
    @apply flex flex-1 justify-end items-center pr-3 pb-6;
  }

  &__children-select {
    @apply flex flex-col items-start justify-start pt-2 text-grey-dark;
    cursor: pointer;

    span {
      font-size: 14px;
    }
  }

  &__menu {
    @apply flex flex-col flex-1 leading-loose pt-8 pb-8;

    @media screen and (max-width: 991px) {
      padding-bottom: 36px;
    }

    a {
      @apply py-3 no-underline flex items-center;
      transition: color 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

      &:hover {
        @apply text-grey-dark;
      }

      &.active {
        @apply relative;

        &::before {
          @apply absolute rounded-full h-8 w-8;
          content: "";
          opacity: 0.8;
          z-index: -1;
        }
      }

      &:first-of-type {
        padding-top: 0;
      }

      &.homeworks-active {
        &::before {
          left: -1px;
          top: 4px;
          background: #88c1e4;
        }
      }

      &.attendances-active {
        &::before {
          left: -2px;
          background: #f6b1c0;
        }
      }

      &.grades-active {
        &::before {
          left: -2px;
          background: #f59e00;
        }
      }

      &.materials-active {
        &::before {
          background: #ee4023;
          opacity: 0.8;
          left: -1px;
          top: 19px;
        }
      }

      &.materials-active:first-child {
        &::before {
          top: 6px;
        }
      }
    }

    img {
      @apply pr-3 w-10;
    }
  }

  &__group {
    @apply leading-normal mb-4;
  }

  &__level {
    @apply leading-normal mb-4;
  }

  &__lector {
    @apply leading-normal;
    margin-bottom: 34px;

    a {
      align-items: flex-start;
    }
  }

  &__lector-name {
    line-height: initial;
  }

  &__links {
    @apply flex items-center flex-col leading-normal text-left pb-8;

    a {
      @apply py-0 mb-1 text-sm no-underline;
      line-height: 1.5rem;
      color: #878787;
      transition: 0.5s color;
      width: 100%;

      &:last-of-type {
        margin-bottom: 0;
      }

      &:hover {
        @apply text-black;
      }
    }

    button {
      @apply text-black;
      text-align: left;
      font-size: 16px;
      display: block;
      width: 100%;
      margin-top: 20px;
      transition: 0.5s color;
      margin-bottom: 75px;

      &:hover {
        color: #878787;
      }
    }

    img {
      @apply pr-2 h-8;
    }
  }

  &__contact-link {
    @apply text-left;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  &__contact-label {
    @apply mt-1;
    font-size: 14px;
    color: #878787;
  }

  &__change-student {
    @apply pb-4 pt-4 flex items-center leading-loose;

    &:hover {
      @apply text-grey-dark;
    }

    img {
      @apply pr-2 w-10;
    }
  }

  &__logout {
    @apply pb-12 pt-4 flex items-center leading-loose;

    &:hover {
      @apply text-grey-dark;
    }

    img {
      @apply pr-2 w-10;
    }
  }
}

.cookies__preferences {
  cursor: pointer;
}
</style>
